import { memo, useRef, useState, useEffect, useMemo, useCallback } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { updateEntry, getEntry, updateTodoStatus, updateTodoAssignee } from '../../api/entries'
import { useAuthStore } from '../../store/auth'
import { AuthenticatedMedia } from './AuthenticatedMedia'
import { BandcampEmbed } from './BandcampEmbed'
import { RedditEmbed } from './RedditEmbed'
import { YouTubeEmbed } from './YouTubeEmbed'
import { ErrorBoundary } from '../ErrorBoundary'
import { getYouTubeVideoId } from '../../utils/youtube'

function getBandcampUrl(url: string): string | null {
  try {
    const u = new URL(url)
    if (/^[^.]+\.bandcamp\.com$/.test(u.hostname) && /^\/(album|track)\//.test(u.pathname)) {
      return url
    }
    return null
  } catch {
    return null
  }
}

function getRedditPostPath(url: string): string | null {
  try {
    const u = new URL(url)
    if (!/^(?:www\.|old\.|np\.)?reddit\.com$/.test(u.hostname)) return null
    if (!/^\/r\/[^/]+\/comments\/[a-zA-Z0-9]+(?:\/[^/]*)?\/?$/.test(u.pathname)) return null
    return u.pathname
  } catch {
    return null
  }
}

type TodoStatusInfo = { todo_status: string | null; todo_assignee_username: string | null; archived_at: string | null }

const TODO_LINK_RE = /^\s*[-*+]\s+\[[ x]\]\s*\[.+?\]\(\/~[^/]+\/entry\/([^)]+)\)\s*$/i

interface Props {
  entryId: string
  authorUsername: string
  content: string
  colorTheme?: Record<string, string> | null
  onUpdate?: (newContent: string) => void
}

const MarkdownRendererInner = memo(function MarkdownRendererInner({ entryId, authorUsername, content, colorTheme, onUpdate }: Props) {
  const username = useAuthStore((s) => s.username)
  const isOwner = username === authorUsername
  const containerRef = useRef<HTMLDivElement>(null)

  const [todoStatuses, setTodoStatuses] = useState<Record<string, TodoStatusInfo>>({})

  const todoLinkMap = useMemo(() => {
    const map = new Map<number, string>()
    content.split('\n').forEach((line, i) => {
      const m = line.match(TODO_LINK_RE)
      if (m) map.set(i, m[1])
    })
    return map
  }, [content])

  const todoIdsKey = useMemo(
    () => Array.from(new Set(todoLinkMap.values())).sort().join('\0'),
    [todoLinkMap],
  )

  useEffect(() => {
    const todoIds = todoIdsKey ? todoIdsKey.split('\0') : []
    if (todoIds.length === 0) {
      setTodoStatuses(prev => Object.keys(prev).length === 0 ? prev : {})
      return
    }
    let cancelled = false
    Promise.all(todoIds.map(id => getEntry(id).catch(() => null))).then(results => {
      if (cancelled) return
      const statuses: Record<string, TodoStatusInfo> = {}
      todoIds.forEach((id, i) => {
        const e = results[i]
        if (e) statuses[id] = { todo_status: e.todo_status, todo_assignee_username: e.todo_assignee_username, archived_at: e.archived_at }
      })
      setTodoStatuses(statuses)
    })
    return () => { cancelled = true }
  }, [todoIdsKey])

  const checkboxLineIndices = useMemo(
    () => content.split('\n')
      .flatMap((line, i) => {
        if (!/^\s*[-*+]\s+\[[ x]\]/i.test(line)) return []
        const todoId = todoLinkMap.get(i)
        if (todoId !== undefined && todoStatuses[todoId]?.archived_at) return []
        return [i]
      }),
    [content, todoLinkMap, todoStatuses],
  )

  const processedContent = useMemo(() => {
    if (todoLinkMap.size === 0 || Object.keys(todoStatuses).length === 0) return content
    const lines = content.split('\n')
    let changed = false
    for (const [lineIndex, todoId] of todoLinkMap.entries()) {
      const status = todoStatuses[todoId]
      if (!status) continue
      if (status.archived_at) { lines[lineIndex] = ''; changed = true; continue }
      const line = lines[lineIndex]
      const isDone = status.todo_status === 'done'
      const newLine = isDone
        ? line.replace(/^(\s*[-*+]\s+)\[ \]/i, '$1[x]')
        : line.replace(/^(\s*[-*+]\s+)\[x\]/i, '$1[ ]')
      if (newLine !== line) { lines[lineIndex] = newLine; changed = true }
    }
    return changed ? lines.join('\n') : content
  }, [content, todoLinkMap, todoStatuses])

  const toggleCheckbox = useCallback(async (lineIndex: number, checked: boolean) => {
    const lines = content.split('\n')
    const line = lines[lineIndex]
    if (checked) {
      lines[lineIndex] = line.replace(/^(\s*[-*+]\s+)\[ \]/, '$1[x]')
    } else {
      lines[lineIndex] = line.replace(/^(\s*[-*+]\s+)\[x\]/i, '$1[ ]')
    }
    const newContent = lines.join('\n')
    await updateEntry(entryId, { content: newContent })
    onUpdate?.(newContent)
  }, [content, entryId, onUpdate])

  const style: React.CSSProperties = colorTheme
    ? ({
        '--color-bg': colorTheme.bg,
        '--color-fg': colorTheme.fg,
        '--color-1': colorTheme.color1,
        '--color-2': colorTheme.color2,
      } as React.CSSProperties)
    : {}

  const components = useMemo<Components>(() => ({
    img: ({ src, alt }) => {
      if (src?.match(/\/~[^/]+\/media\//)) {
        return <AuthenticatedMedia url={src} mimetype="image/unknown" name={alt ?? ''} />
      }
      return <img src={src} alt={alt ?? ''} style={{ maxWidth: '100%' }} />
    },
    a: ({ href, children }) => {
      const videoId = href ? getYouTubeVideoId(href) : null
      const isAutolink =
        typeof children === 'string'
          ? children === href
          : Array.isArray(children) && children.length === 1 && children[0] === href
      if (videoId && isAutolink) return <YouTubeEmbed videoId={videoId} />
      const bandcampUrl = href ? getBandcampUrl(href) : null
      if (bandcampUrl && isAutolink) return <BandcampEmbed url={bandcampUrl} />
      const redditPath = href ? getRedditPostPath(href) : null
      if (redditPath && href && isAutolink) return <RedditEmbed url={href} path={redditPath} />
      const todoMatch = href?.match(/^\/~[^/]+\/entry\/([^)]+)$/)
      if (todoMatch && todoStatuses[todoMatch[1]]?.todo_status === 'done') {
        return <a href={href} style={{ textDecoration: 'line-through' }}>{children}</a>
      }
      return <a href={href}>{children}</a>
    },
    input: ({ checked, ...props }) => {
      if (props.type === 'checkbox') {
        return (
          <input
            {...props}
            type="checkbox"
            checked={checked}
            disabled={!isOwner}
            onChange={async (e) => {
              if (!isOwner) return
              const boxes = containerRef.current?.querySelectorAll('input[type="checkbox"]')
              const clickedIndex = boxes ? Array.from(boxes).indexOf(e.target as HTMLInputElement) : -1
              const lineIndex = checkboxLineIndices[clickedIndex]
              if (lineIndex === undefined) return
              const todoId = todoLinkMap.get(lineIndex)
              if (todoId !== undefined) {
                if (e.target.checked) {
                  await updateTodoStatus(todoId, 'done')
                  const currentAssignee = todoStatuses[todoId]?.todo_assignee_username
                  if (!currentAssignee && username) {
                    await updateTodoAssignee(todoId, username)
                    setTodoStatuses(prev => ({ ...prev, [todoId]: { todo_status: 'done', todo_assignee_username: username, archived_at: prev[todoId]?.archived_at ?? null } }))
                  } else {
                    setTodoStatuses(prev => ({ ...prev, [todoId]: { ...prev[todoId], todo_status: 'done' } }))
                  }
                } else {
                  await updateTodoStatus(todoId, 'wip')
                  setTodoStatuses(prev => ({ ...prev, [todoId]: { ...prev[todoId], todo_status: 'wip' } }))
                }
              } else {
                await toggleCheckbox(lineIndex, e.target.checked)
              }
            }}
          />
        )
      }
      return <input {...props} />
    },
  }), [isOwner, todoStatuses, checkboxLineIndices, todoLinkMap, username, toggleCheckbox])

  return (
    <div className="markdown-body" style={style} ref={containerRef}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processedContent}
      </ReactMarkdown>
    </div>
  )
})

export function MarkdownRenderer(props: Props) {
  return (
    <ErrorBoundary fallback={<pre style={{ whiteSpace: 'pre-wrap', opacity: 0.7, fontSize: '0.9em' }}>{props.content}</pre>}>
      <MarkdownRendererInner {...props} />
    </ErrorBoundary>
  )
}
